import { type ReactNode } from 'react';

import { cn } from '../../lib/cn';

/**
 * docs/02-DESIGN-SYSTEM.md §4.2 — "Illustration + message + optional action",
 * and docs/06-FRONTEND-SPEC.md §7: an empty list gets an explanation and a
 * clear-filters action, never a blank panel.
 *
 * Worth getting right rather than treating as an edge case: an empty result on
 * the Documents or Personnel page is usually over-filtering, and the way out is
 * the action this renders. §8 of the frontend spec also calls zero-result
 * searches the cheapest signal for what content is missing.
 */
export interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  /** Decorative illustration or icon; hidden from assistive technology. */
  illustration?: ReactNode;
  /** e.g. a "clear filters" button. */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  title,
  description,
  illustration,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      // The list this replaces is usually inside a live region already; this
      // makes sure the change of state is announced when it is not.
      role="status"
      className={cn(
        'flex flex-col items-center gap-4 rounded-xl bg-surface-alt px-6 py-16 text-center',
        className,
      )}
    >
      {illustration ? (
        <div aria-hidden="true" className="text-muted">
          {illustration}
        </div>
      ) : null}

      <p className="text-h4 text-primary">{title}</p>

      {description ? <p className="max-w-prose text-body text-secondary">{description}</p> : null}

      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}
